import React from "react";
import { Tag, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { contrastTextColor } from "@/utils/color.utils";
import { chipAffordanceClasses, chipSizeClasses } from "./chip.styles";

/** Neutral background used when a tag type has no color set. */
export const DEFAULT_TAG_COLOR = "#e5e7eb";

interface TagChipProps {
	name: string;
	/** Hex #RRGGBB background from the tag type; neutral gray when unset. */
	color?: string | null;
	/** "md" (settings pages, default) or "sm" (compact, for list-view rows). */
	size?: "md" | "sm";
	/** Show a leading tag icon inside the chip. */
	showIcon?: boolean;
	title?: string;
	className?: string;
	/** Small trailing adornment rendered after the name (e.g. an auto-derived marker). */
	indicator?: React.ReactNode;
	/** When set, a small ✕ is rendered inside the chip (e.g. removable selection). */
	onRemove?: () => void;
	removeDisabled?: boolean;
}

/**
 * Trello-style tag label: colored background (from the org tag type) with
 * automatically chosen black/white text for best contrast.
 */
const TagChip = ({
	name,
	color,
	size = "md",
	showIcon,
	title,
	className,
	indicator,
	onRemove,
	removeDisabled,
}: TagChipProps) => {
	const background = color || DEFAULT_TAG_COLOR;

	return (
		<span
			title={title}
			className={cn(
				"inline-flex max-w-full items-center truncate rounded-[2px] font-medium",
				chipSizeClasses(size),
				onRemove && "pr-1",
				className
			)}
			style={{ backgroundColor: background, color: contrastTextColor(background) }}
		>
			{showIcon && <Tag size={size === "sm" ? 11 : 13} className="shrink-0" />}
			<span className="truncate">{name}</span>
			{indicator}
			{onRemove && (
				<button
					type="button"
					onClick={onRemove}
					disabled={removeDisabled}
					aria-label={`Ta bort ${name}`}
					title="Ta bort"
					className={cn(
						chipAffordanceClasses,
						"rounded border-0 bg-transparent text-current opacity-60 hover:bg-black/10 hover:opacity-100 disabled:pointer-events-none"
					)}
				>
					<X size={13} />
				</button>
			)}
		</span>
	);
};

export default TagChip;
